import { AlertTriangle, Ban } from 'lucide-react'
import { type FC } from 'react'

import { Badge, Tooltip } from 'slab'
import {
  type BrokersEscalationReason,
  type BrokersEscalationReasonCode,
  type BrokersExclusionReason,
  type BrokersExclusionReasonCode,
} from 'utils/web_api.schemas'

// Type-safe labels for exclusion reason codes - TypeScript will error if any code is missing
const EXCLUSION_LABELS: Record<BrokersExclusionReasonCode, string> = {
  too_early_for_coverage: 'Too early for coverage',
  date_of_month_restriction: 'Date restriction',
  outside_business_hours: 'Outside business hours',
  invalid_enrollment_state: 'Invalid enrollment state',
  premiums_not_created: 'Premiums not created',
  missing_enrollment_event: 'Missing enrollment event',
  medical_not_enrolled: 'Medical not enrolled',
  carrier_member_id_missing: 'Carrier member ID missing',
  binder_payment_already_made: 'Binder payment already made',
  autopay_already_enabled: 'Autopay already enabled',
  existing_820_payment: 'Existing 820 payment',
  unsupported_open_enrollment: 'Unsupported open enrollment',
  unsupported_special_enrollment: 'Unsupported special enrollment',
  unsupported_ichra_invite: 'Unsupported ICHRA invite',
  custom: 'Custom reason',
}

// Type-safe labels for escalation reason codes - TypeScript will error if any code is missing
const ESCALATION_LABELS: Record<BrokersEscalationReasonCode, string> = {
  coverage_start_date_in_past: 'Coverage start date in past',
  non_binary_gender: 'Non-binary gender',
  invalid_ssn: 'Invalid SSN',
  covered_participants_mismatch: 'Participants mismatch',
  enrollment_already_paid: 'Enrollment already paid',
  missing_enrollment_event: 'Missing enrollment event',
  employer_not_approved: 'Employer not approved',
  unsupported_open_enrollment: 'Unsupported open enrollment',
  unsupported_special_enrollment: 'Unsupported special enrollment',
  unsupported_ichra_invite: 'Unsupported ICHRA invite',
  custom: 'Custom reason',
}

// Get a human-readable label for a reason code
const getExclusionLabel = (code: BrokersExclusionReasonCode): string => {
  return EXCLUSION_LABELS[code]
}

const getEscalationLabel = (code: BrokersEscalationReasonCode): string => {
  return ESCALATION_LABELS[code]
}

type ReasonBadgeProps =
  | { reason: BrokersExclusionReason; type: 'exclusion' }
  | { reason: BrokersEscalationReason; type: 'escalation' }

const ReasonBadge: FC<ReasonBadgeProps> = ({ reason, type }) => {
  const label = type === 'exclusion'
    ? getExclusionLabel((reason as BrokersExclusionReason).code)
    : getEscalationLabel((reason as BrokersEscalationReason).code)
  const Icon = type === 'exclusion' ? Ban : AlertTriangle
  const badgeType = type === 'exclusion' ? 'slate' : 'warning'

  return (
    <Tooltip content={reason.message || label} side='top'>
      <div className='inline-block'>
        <Badge type={badgeType} bordered leftIcon={Icon}>
          {label}
        </Badge>
      </div>
    </Tooltip>
  )
}

interface TaskReasonBadgesProps {
  exclusionReasons?: BrokersExclusionReason[]
  escalationReasons?: BrokersEscalationReason[]
}

export const TaskReasonBadges: FC<TaskReasonBadgesProps> = ({
  exclusionReasons,
  escalationReasons,
}) => {
  const hasExclusionReasons = exclusionReasons && exclusionReasons.length > 0
  const hasEscalationReasons = escalationReasons && escalationReasons.length > 0

  if (!hasExclusionReasons && !hasEscalationReasons) {
    return null
  }

  return (
    <div className='space-y-3'>
      {hasExclusionReasons && (
        <div className='space-y-2'>
          <span className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
            Exclusion Reasons
          </span>
          <div className='flex flex-wrap gap-2'>
            {exclusionReasons.map((reason, index) => (
              <ReasonBadge key={`exclusion-${reason.code}-${index}`} reason={reason} type='exclusion' />
            ))}
          </div>
        </div>
      )}
      {hasEscalationReasons && (
        <div className='space-y-2'>
          <span className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
            Escalation Reasons
          </span>
          <div className='flex flex-wrap gap-2'>
            {escalationReasons.map((reason, index) => (
              <ReasonBadge key={`escalation-${reason.code}-${index}`} reason={reason} type='escalation' />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
